import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Alert, Autocomplete, Box, CircularProgress, Snackbar, TextField } from '@mui/material'
import { Add as IconAdd, CheckCircle as IconCheckCircle } from '@mui/icons-material'
import DialogYesNo from './DialogYesNo'

// Los autocompletados de la jerarquia Modelo -> Area -> Objetivo -> Criterio
// se comportan asi:
//   - sin texto (o al abrir el desplegable): listado completo, en orden
//     alfabetico;
//   - con 1 o 2 letras: se filtra la lista local y se muestran los 50 primeros;
//   - al llegar a 3 letras: se busca automaticamente en el servidor (tambien
//     con Enter, para quien no espere la peticion);
//   - con 3 letras o mas, si el texto no coincide exactamente con un registro
//     existente, aparece al final un boton fijo 'Registrar Nuevo "<texto>"'
//     que queda siempre visible al final de la lista.
// El dialogo modal de MUI usa z-index 1300. Para que el desplegable del
// autocompletado sea visible cuando este dentro de un Dialog modal, su z-index
// debe ser superior a 1300 (1400).
export const LARGO_MINIMO_BUSQUEDA = 3
export const MAXIMO_OPCIONES = 50
export const UMBRAL_TOP_INICIAL = 20
const PREFIJO_NUEVO = 'Registrar Nuevo "'
const Z_INDEX_LISTA = 1400;
const SUFIJO_NUEVO = '"'

// Un solo estilo para las tres listas: el listbox es el contenedor que
// desplaza, de modo que la ultima fila (Registrar Nuevo) queda anclada al
// fondo de la lista aunque haya hundreds de opciones encima.
const ESTILO_LISTBOX = {
    maxHeight: '40vh',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 0,
    '& > li': {
        paddingTop: 6,
        paddingBottom: 6,
    },
}

// Numero de caracteres que hay que escribir para que el texto se considere
// apto para registrar un registro nuevo.
const textoAceptable = (texto) => (texto || '').trim().length >= LARGO_MINIMO_BUSQUEDA

// Quita tildes y pasa a minusculas para comparar: al escribir "Fisi" tiene que
// encontrar "Física" y "cognitiva" tiene que encontrar "Cognitiva".
const normalizar = (texto) => (texto || '')
    .toString()
    .trim()
    .toLowerCase()
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')

const AutocompleteRegistro = (props) => {
    const {
        label,
        name,
        disabled,
        size,
        texto,
        idSeleccionado,
        opciones,
        getLabel,
        onTexto,
        onSeleccionar,
        onRegistrarNuevo = null,
        onBuscar = null,
        puedeRegistrar = true,
        mensajeFaltaPadre = null,
        // Nombre del hijo que se esta registrando ("area", "objetivo",
        // "criterio"): arma los textos del dialogo de confirmacion y del aviso.
        tipoRegistro = null,
        // Umbral de items a partir del cual se asume que la lista inicial fue
        // truncada por el TOP 20 del backend y se requiere buscar en el servidor.
        umbralBusquedaServidor = UMBRAL_TOP_INICIAL,
        required = false,
        error = false,
        helperText = "",
        InputLabelProps,
    } = props

    // Texto tal como lo escribe el usuario. Al elegir la opcion 'Registrar
    // Nuevo', MUI escribe su propio rotulo en el input; este ref conserva lo
    // que el usuario escribio para registrar eso y no el rotulo de la opcion.
    const textoUsuarioRef = useRef(texto == null ? "" : texto)
    // Ultimo texto que este componente propuso al padre: sirve para distinguir
    // los cambios que vienen del input de los que llegan desde afuera (limpiar
    // los hijos al cambiar de padre, por ejemplo).
    const textoPropuestoRef = useRef(texto == null ? "" : texto)
    // Solo se consulta el servidor lo que el usuario escribe a mano. Si no, al
    // cargar un criterio ya guardado el desplegable quedaria reducido a la unica
    // coincidencia del texto guardado en lugar de mostrar el listado del padre.
    const escritoAManoRef = useRef(false)
    // Ref al Autocomplete: sirve para cerrar su lista desplegable antes de abrir
    // el dialogo de confirmacion.
    const autocompleteRef = useRef(null)

    // Marca el onChange que dispara el propio cierre de la lista para poder
    // ignorarlo: si no, el blur manda el texto del input como si el usuario
    // hubiera elegido un registro y se pierde lo escrito.
    const ignorarChangeRef = useRef(false)

    // Cierra la lista desplegable antes de abrir el dialogo de confirmacion.
    // Si se deja abierta, el Popper se dibuja encima y tapa el dialogo.
    const cerrarLista = useCallback(() => {
        const autocomplete = autocompleteRef.current;
        if (autocomplete == null) return;

        if (typeof autocomplete.close === 'function') {
            autocomplete.close();
            return;
        }

        // Sin close() se recurre a blur(), que ademas emite un onChange propio.
        ignorarChangeRef.current = true;
        if (typeof autocomplete.blur === 'function') autocomplete.blur();
    }, [])
    const [resultados, setResultados] = useState(null)
    const [buscando, setBuscando] = useState(false)
    // Texto a registrar a la espera de la confirmacion del usuario.
    const [porRegistrar, setPorRegistrar] = useState(null)
    const [mensaje, setMensaje] = useState(null)

    const nombreTipo = (tipoRegistro || 'registro').toString()
    const avisar = useCallback((texto, severity) => {
        setMensaje({ texto: texto, severity: severity || 'info' })
    }, [])

    // El padre recrea los callbacks en cada render: se guardan en refs para que
    // los efectos y los manejadores no se vuelvan a disparar sin motivo.
    const onBuscarRef = useRef(onBuscar)
    onBuscarRef.current = onBuscar
    const onRegistrarNuevoRef = useRef(onRegistrarNuevo)
    onRegistrarNuevoRef.current = onRegistrarNuevo
    const onTextoRef = useRef(onTexto)
    onTextoRef.current = onTexto
    const onSeleccionarRef = useRef(onSeleccionar)
    onSeleccionarRef.current = onSeleccionar

    // Escribe el texto en el input y deja constancia de que el cambio salio de
    // aqui (no del padre).
    const proponerTexto = useCallback((valor) => {
        const t = valor == null ? "" : valor
        textoPropuestoRef.current = t
        onTextoRef.current(t)
    }, [])

    // Si el padre cambia el texto por su cuenta (limpiar los hijos al cambiar
    // de padre, o el texto guardado que llega del servidor), se vuelve a la
    // lista local completa y no se busca en el servidor.
    useEffect(() => {
        const t = texto == null ? "" : texto
        if (t !== textoPropuestoRef.current) {
            textoUsuarioRef.current = t
            textoPropuestoRef.current = t
            escritoAManoRef.current = false
            setResultados(null)
        }
    }, [texto])

    const textoOpcion = useCallback((opcion) => {
        if (opcion == null) return ""
        const valor = getLabel ? getLabel(opcion) : (opcion.nombre || opcion.descripcion || "")
        return valor == null ? "" : String(valor)
    }, [getLabel])

    // Busqueda automatica en el servidor al llegar a las tres letras.
    // Solo se consulta a la base de datos si la lista inicial tiene al menos
    // umbralBusquedaServidor (20) items; si tiene menos de 20, la lista local
    // ya contiene todos los registros disponibles y el filtrado en memoria es suficiente.
    useEffect(() => {
        const escrito = textoUsuarioRef.current.trim()
        const buscar = onBuscarRef.current
        const cantidadInicial = (opciones || []).filter((o) => o != null && o.id > 0).length
        const listaTruncada = cantidadInicial >= umbralBusquedaServidor

        if (buscar == null || !escritoAManoRef.current || !textoAceptable(escrito) || !listaTruncada) {
            setResultados(null)
            setBuscando(false)
            return
        }

        let vigente = true
        setBuscando(true)

        // Pequena espera para no pedir en cada tecla.
        const timer = setTimeout(() => {
            Promise.resolve(buscar(escrito))
                .then((r) => { if (vigente) setResultados(Array.isArray(r) ? r : []) })
                .catch((err) => {
                    console.log("Err: ", err)
                    if (vigente) setResultados([])
                })
                .finally(() => { if (vigente) setBuscando(false) })
        }, 250)

        return () => {
            vigente = false
            clearTimeout(timer)
        }
    }, [texto, opciones, umbralBusquedaServidor])

    // Mientras hay resultados del servidor mandan ellos; si no, la lista local
    // completa que el padre ya tiene cargada.
    const listaBase = useMemo(() => {
        const fuente = resultados != null ? resultados : (opciones || [])
        return fuente.filter((o) => o != null && o.id > 0)
    }, [resultados, opciones])

    // El valor seleccionado se busca siempre en la lista completa del padre
    // (no en la filtrada): si no, el input parpadea al escribir.
    const seleccionado = useMemo(
        () => (opciones || []).find((o) => o != null && o.id === idSeleccionado) || null,
        [opciones, idSeleccionado])

    // La marca de "ya registrado" solo se muestra mientras el input siga
    // mostrando el registro elegido: si el usuario escribe otra cosa, el check
    // desapare porque ese texto todavia no existe.
    const registrado = (seleccionado != null
        && normalizar(texto) === normalizar(textoOpcion(seleccionado)))

    const getOptionLabel = useCallback((opcion) => {
        if (opcion == null) return ""
        if (opcion.__nuevo) return PREFIJO_NUEVO + (opcion.__texto || "") + SUFIJO_NUEVO
        return textoOpcion(opcion)
    }, [textoOpcion])

    // Al elegir 'Registrar Nuevo' solo se abre el dialogo de confirmacion: el
    // registro se hace recien cuando el usuario responde Si.
    const registrarNuevo = useCallback((textoNuevo) => {
        const t = (textoNuevo || "").trim()

        if (!textoAceptable(t) || onRegistrarNuevoRef.current == null) return

        // Sin el padre elegido no se puede crear el hijo: se avisa y se devuelve
        // el texto al input para que no se pierda lo escrito.
        if (!puedeRegistrar) {
            cerrarLista();
            avisar(mensajeFaltaPadre || "Seleccione primero el padre.", 'warning')
            proponerTexto(t)
            return
        }

        cerrarLista();

        // El input quedo con el rotulo de la opcion: se restaura el texto.
        proponerTexto(t)
        setPorRegistrar(t)
    }, [puedeRegistrar, mensajeFaltaPadre, avisar, proponerTexto, cerrarLista])

    const confirmarRegistro = useCallback(() => {
        const t = porRegistrar
        if (t == null || onRegistrarNuevoRef.current == null) return

        Promise.resolve(onRegistrarNuevoRef.current(t))
            .then((registrado) => {
                if (registrado != null && registrado.id > 0) {
                    textoUsuarioRef.current = textoOpcion(registrado)
                    proponerTexto(textoOpcion(registrado))
                    onSeleccionarRef.current(registrado)
                    avisar('Nuevo ' + nombreTipo + ' registrado: "' + t + '"', 'success')
                }
                else {
                    avisar('No se pudo registrar el ' + nombreTipo + ': "' + t + '"', 'error')
                }
            })
            .catch((err) => {
                console.log("Err: ", err)
                avisar('No se pudo registrar el ' + nombreTipo + ': "' + t + '"', 'error')
            })
    }, [porRegistrar, textoOpcion, proponerTexto, avisar, nombreTipo])

    // Firma de MUI v5: (evento, valor, motivo). El motivo vale 'input' cuando lo
    // teclea el usuario, 'reset' cuando el componente escribe el texto (elegir
    // una opcion, limpiar) y 'clear' cuando se borra con la X.
    const onInputChange = useCallback((event, nuevoValor, reason) => {
        const t = nuevoValor == null ? "" : nuevoValor

        // Solo lo tecleado por el usuario dispara la busqueda en el servidor.
        escritoAManoRef.current = (reason === 'input')

        // Al elegir una opcion MUI escribe su texto en el input: ese rotulo no
        // es lo que escribio el usuario, asi que no se guarda como tal.
        if (t.indexOf(PREFIJO_NUEVO) !== 0) {
            textoUsuarioRef.current = t
            proponerTexto(t)
        }

        // Con menos de tres letras vuelve a mandar la lista local.
        if (!textoAceptable(t.trim())) setResultados(null)
    }, [proponerTexto])

    const onChange = useCallback((event, newValue, reason) => {
        // Cambio provocado por el blur del cierre de la lista: se descarta para
        // que no se pierda el texto escrito.
        if (reason === 'blur' && ignorarChangeRef.current) {
            ignorarChangeRef.current = false;
            return;
        }

        if (newValue != null && newValue.__nuevo) {
            const t = textoUsuarioRef.current.trim()
            proponerTexto(t)
            registrarNuevo(t)
            return
        }

        if (newValue == null) {
            textoUsuarioRef.current = ""
            proponerTexto("")
            onSeleccionarRef.current(null)
            return
        }

        textoUsuarioRef.current = textoOpcion(newValue)
        proponerTexto(textoOpcion(newValue))
        onSeleccionarRef.current(newValue)
    }, [registrarNuevo, textoOpcion, proponerTexto])

    const onKeyDown = useCallback((event) => {
        if (event.key !== "Enter") return

        const t = textoUsuarioRef.current.trim()
        if (t.length === 0) return

        // Coincidencia exacta: se elige el registro y no se crea otro.
        const exacta = listaBase.find(
            (o) => normalizar(textoOpcion(o)) === normalizar(t))

        if (exacta) {
            event.preventDefault()
            textoUsuarioRef.current = textoOpcion(exacta)
            proponerTexto(textoOpcion(exacta))
            onSeleccionarRef.current(exacta)
            return
        }

        // Sin coincidencia exacta y con tres letras o mas: se ofrece registrar.
        if (textoAceptable(t) && onRegistrarNuevoRef.current != null) {
            event.preventDefault()
            registrarNuevo(t)
            return
        }

        // Con 1 o 2 letras Enter solo cierra el desplegable: el filtro de las 50
        // primeras ya se ve mientras se escribe.
    }, [listaBase, registrarNuevo, textoOpcion, proponerTexto])

    const filterOptions = useCallback((listaRecibida, state) => {
        const t = (state.inputValue || "").trim()

        // Sin texto: todo el listado disponible, en orden alfabetico.
        const ordenada = [...listaBase].sort((a, b) =>
            textoOpcion(a).localeCompare(textoOpcion(b)))

        if (t.length === 0) return ordenada

        const buscado = normalizar(t);

        // Con texto: coincidencia parcial y los 50 primeros.
        const filtrada = ordenada
            .filter((o) => normalizar(textoOpcion(o)).indexOf(buscado) >= 0)
            .slice(0, MAXIMO_OPCIONES)

        // Con 3 letras o mas y sin coincidencia exacta se ofrece crear el
        // registro como ultimo item, con el boton de 'Agregar'.
        if (textoAceptable(t) && onRegistrarNuevoRef.current != null && puedeRegistrar
            && !filtrada.some((o) => normalizar(textoOpcion(o)) === buscado)) {
            filtrada.push({ id: -1, __nuevo: true, __texto: t })
        }

        return filtrada
    }, [listaBase, puedeRegistrar, textoOpcion])

    const renderOption = useCallback((props, option) => {
        if (option != null && option.__nuevo) {
            return (
                <Box component="li" {...props} key="__nuevo"
                    sx={{
                        // Boton fijo al final: sigue visible aunque la lista
                        // larga se desplace.
                        position: 'sticky',
                        bottom: 0,
                        zIndex: 1,
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        backgroundColor: 'background.paper',
                        borderTop: 1,
                        borderColor: 'divider',
                        color: 'primary.main',
                        fontWeight: 600,
                        '&.Mui-focused': {
                            backgroundColor: 'action.hover',
                            color: 'primary.main',
                        },
                    }}>
                    <IconAdd fontSize="small" />
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {PREFIJO_NUEVO}{option.__texto}{SUFIJO_NUEVO}
                    </span>
                </Box>
            )
        }

        return (
            <li {...props} key={option.id}>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {textoOpcion(option)}
                </span>
            </li>
        )
    }, [textoOpcion])

    return (
        <>
            <Autocomplete
                ref={autocompleteRef}
                freeSolo
                name={name}
                size={size || 'small'}
                disabled={disabled}
                value={seleccionado}
                inputValue={texto == null ? '' : texto}
                options={listaBase}
                autoHighlight
                openOnFocus
                loading={buscando}
                noOptionsText={buscando ? 'Buscando...' : 'Sin resultados'}
                getOptionLabel={getOptionLabel}
                isOptionEqualToValue={(opcion, valor) =>
                    valor != null && opcion != null && opcion.id === valor.id}
                filterOptions={filterOptions}
                onInputChange={onInputChange}
                onChange={onChange}
                onKeyDown={onKeyDown}
                renderOption={renderOption}
                componentsProps={{
                    listbox: { sx: ESTILO_LISTBOX },
                    paper: { sx: { borderRadius: 1 } },
                    // El Popper del autocompletado queda por debajo del dialogo
                    // de confirmacion: si no, la lista desplegada se dibuja
                    // encima y tapa el texto y los botones Si/No.
                    popper: { style: { zIndex: Z_INDEX_LISTA } },
                }}
                renderInput={(params) => (
                    <TextField
                        {...params}
                        required={required}
                        error={error}
                        helperText={helperText}
                        label={label}
                        variant="outlined"
                        InputLabelProps={{
                            shrink: true,
                            ...InputLabelProps,
                            ...params.InputLabelProps,
                        }}
                        inputProps={{
                            ...params.inputProps,
                            autoComplete: 'new-password',
                        }}
                        InputProps={{
                            ...params.InputProps,
                            endAdornment: (
                                <>
                                    {/* Marca de registro ya existente. */}
                                    {registrado && (
                                        <IconCheckCircle fontSize="small"
                                            sx={{ color: 'success.main', mr: 0.5 }} />
                                    )}
                                    {buscando && (
                                        <CircularProgress color="inherit" size={16} sx={{ mr: 0.5 }} />
                                    )}
                                    {params.InputProps?.endAdornment}
                                </>
                            ),
                        }}
                    />
                )}
            />

            {/* Confirmacion antes de crear el hijo que se esta escribiendo. */}
            <DialogYesNo
                open={porRegistrar != null}
                openClose={() => setPorRegistrar(null)}
                titulo={'Registrar nuevo ' + nombreTipo}
                texto={'Desea registrar el nuevo ' + nombreTipo + ' "' + (porRegistrar || '') + '"?'}
                setYes={confirmarRegistro}
                setNo={() => setPorRegistrar(null)}
                sx={{ zIndex: 1500 }}
            />

            {/* Avisos: falta el padre, registro hecho o error. */}
            <Snackbar
                open={mensaje != null}
                autoHideDuration={4000}
                onClose={() => setMensaje(null)}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ zIndex: 1600 }}
            >
                <Alert
                    severity={(mensaje && mensaje.severity) || 'info'}
                    variant="filled"
                    onClose={() => setMensaje(null)}
                >
                    {mensaje ? mensaje.texto : ''}
                </Alert>
            </Snackbar>
        </>
    )
}

export default AutocompleteRegistro